import { useEffect, useState, useRef } from "react";

import {
  Button,
  Icon,
  Intent,
  OverlayToaster,
  Position,
  Spinner,
} from "@blueprintjs/core";

import { useWorkspaceSlug } from "@/components/helpers/custom-hooks/use-workspace-slug";
import { Box, Flex } from "@/components/layout/flexbox";
import NotificationMessageForm from "@/components/pieces/roleplays/sharing/NotificationMessageForm";
import { GeneralAccess } from "@/components/pieces/roleplays/sharing/scenario-share/GeneralAccess";
import { PermissionSelector } from "@/components/pieces/roleplays/sharing/scenario-share/PermissionSelector";
import { ScenarioProgram } from "@/components/pieces/roleplays/sharing/scenario-share/ScenarioProgram";
import { SharedPill } from "@/components/pieces/roleplays/sharing/scenario-share/SharedPill";
import { ScenarioShareMultiselect } from "@/components/pieces/roleplays/sharing/ScenarioShareMultiselect";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import { getUser } from "@/components/session/JwtTokenManager";
import { LIGHT_GRAY, MIDTEXT_COLOR } from "@/css/constants";
import {
  PermissionLevel,
  ResourceType,
  CreateResourcePermissionsInput,
  UpdateResourcePermissionsInput,
  ScenarioDocument,
  VisibilityScope,
  useResourcePermissionsLazyQuery,
  useCreateResourcePermissionsMutation,
  useUpdateResourcePermissionsMutation,
  ResourcePermissionsQuery,
} from "@/graphql";
import { SharePermissionState } from "@/types/programs";
import { FullScenario } from "@/types/roleplays";
import { ensureNonNullArray, formatErrors } from "@/utils/array";
import { NonNullableArrayItem } from "@/utils/types";

type ResourcePermission = NonNullableArrayItem<
  NonNullable<ResourcePermissionsQuery["resourcePermissions"]>
>;

type ResourcePermissionUser = NonNullable<ResourcePermission["user"]> & {
  originalPermissionLevel?: PermissionLevel | null;
  shareStatus?: SharePermissionState;
  remove?: boolean;
  permissionId?: string;
  isOwner?: boolean;
};

type ResourcePermissionGroup = NonNullable<
  ResourcePermission["workspaceGroup"]
> & {
  originalPermissionLevel?: PermissionLevel | null;
  shareStatus?: SharePermissionState;
  remove?: boolean;
  permissionId?: string;
};

type ResourcePermissionProgram = NonNullable<ResourcePermission["program"]> & {
  originalPermissionLevel?: PermissionLevel | null;
  shareStatus?: SharePermissionState;
  permissionId?: string;
};

// Process permissions data and categorize by type
const processResourcePermissions = (permissions: ResourcePermission[]) => {
  const result: {
    users: ResourcePermissionUser[];
    groups: ResourcePermissionGroup[];
    programs: ResourcePermissionProgram[];
    owner?: ResourcePermissionUser;
  } = {
    users: [],
    groups: [],
    programs: [],
  };

  permissions.forEach((permission) => {
    if (permission.user) {
      const userEntry: ResourcePermissionUser = {
        ...permission.user,
        shareStatus: permission.permissionLevel as SharePermissionState,
        originalPermissionLevel: permission.permissionLevel,
        permissionId: permission.id,
        isOwner: permission.accessScope === "Owner",
      };

      if (permission.accessScope === "Owner") {
        // Add owner to the beginning of the array
        result.users.unshift(userEntry);
      } else {
        result.users.push(userEntry);
      }
    } else if (permission.workspaceGroup) {
      result.groups.push({
        ...permission.workspaceGroup,
        shareStatus: permission.permissionLevel as SharePermissionState,
        originalPermissionLevel: permission.permissionLevel,
        permissionId: permission.id,
      });
    } else if (permission.program) {
      result.programs.push({
        ...permission.program,
        shareStatus: permission.permissionLevel as SharePermissionState,
        originalPermissionLevel: permission.permissionLevel,
        permissionId: permission.id,
      });
    }
  });

  return result;
};

interface ScenarioShareModalProps {
  scenario: FullScenario;
  isOpen: boolean;
  onClose: () => void;
  loading: boolean;
}

export function ScenarioShareModal({
  scenario,
  isOpen,
  onClose,
  loading: externalLoading,
}: ScenarioShareModalProps) {
  const [copied, setCopied] = useState<boolean | null>(null);
  const toasterRef = useRef<OverlayToaster>(null);
  const [showAllPrograms, setShowAllPrograms] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [sharedUsers, setSharedUsers] = useState<ResourcePermissionUser[]>([]);
  const [sharedGroups, setSharedGroups] = useState<ResourcePermissionGroup[]>(
    [],
  );

  const [sharedPrograms, setSharedPrograms] = useState<
    ResourcePermissionProgram[]
  >([]);

  // Get visibilityScope from scenario
  const [visibilityScope, setVisibilityScope] = useState<VisibilityScope>(
    scenario.visibilityScope || VisibilityScope.User,
  );

  const [fetchResourcePermissions, { loading: permissionsLoading }] =
    useResourcePermissionsLazyQuery({
      fetchPolicy: "network-only",
      onCompleted: (response) => {
        if (response?.resourcePermissions) {
          // Process the permissions data using the separate function
          const { users, groups, programs } = processResourcePermissions(
            ensureNonNullArray(response.resourcePermissions),
          );

          setSharedUsers(users);
          setSharedGroups(groups);
          setSharedPrograms(programs);
        }
      },
    });

  const onError = (error: Error) => {
    // Show error toast notification
    toasterRef.current?.show({
      message: "Failed to update permissions",
      intent: Intent.DANGER,
    });
    console.error("Error permissions:", error);
  };

  const [createResourcePermissions, { loading: createLoading }] =
    useCreateResourcePermissionsMutation({
      onCompleted: (response) => {
        if (response?.createResourcePermissions?.ok) {
          // Show success toast notification
          toasterRef.current?.show({
            message: "Permissions Updated Successfully",
            intent: Intent.SUCCESS,
          });

          // Refresh permissions data
          if (scenario?.id) {
            fetchResourcePermissions({
              variables: {
                resourceType: ResourceType.Scenario,
                resourceId: scenario.id,
              },
            });
          }

          // Reset state and close modal
          setSelectedUsers([]);
          setSelectedGroups([]);
          setIsNotified(true);
          setMessage("");
          setHasChanges(false);
          handleClose();
        } else {
          const errorMessage = formatErrors(
            response?.createResourcePermissions?.errors,
          );
          onError(
            new Error(
              errorMessage ||
                "An error occurred while creating resource permissions",
            ),
          );
        }
      },
      onError,
      refetchQueries: [
        {
          query: ScenarioDocument,
          variables: { scenarioSlug: scenario?.slug },
        },
      ],
    });

  const [updateResourcePermissions, { loading: updateLoading }] =
    useUpdateResourcePermissionsMutation({
      onCompleted: (response) => {
        if (response?.updateResourcePermissions?.ok) {
          // Show success toast notification
          toasterRef.current?.show({
            message: "Permissions updated successfully",
            intent: Intent.SUCCESS,
          });

          // Refresh permissions data
          if (scenario?.id) {
            fetchResourcePermissions({
              variables: {
                resourceType: ResourceType.Scenario,
                resourceId: scenario.id,
              },
            });
          }

          setHasChanges(false);
          handleClose();
        } else {
          const errorMessage = formatErrors(
            response?.updateResourcePermissions?.errors,
          );
          onError(
            new Error(
              errorMessage ||
                "An error occurred while updating resource permissions",
            ),
          );
        }
      },
      onError,
      refetchQueries: [
        {
          query: ScenarioDocument,
          variables: { scenarioSlug: scenario?.slug },
        },
      ],
    });

  useEffect(() => {
    if (isOpen && scenario?.id) {
      fetchResourcePermissions({
        variables: {
          resourceType: ResourceType.Scenario,
          resourceId: scenario.id,
        },
      });

      // Update visibility scope from scenario whenever it changes
      if (scenario.visibilityScope) {
        setVisibilityScope(scenario.visibilityScope);
      }
    }
  }, [isOpen, scenario]);

  const programs = sharedPrograms.length > 0 ? sharedPrograms : [];
  const displayedPrograms = showAllPrograms ? programs : programs.slice(0, 5);
  const hasMorePrograms = programs.length > 5;

  const [isNotified, setIsNotified] = useState(true);
  const [message, setMessage] = useState("");
  const [selectedPermission, setSelectedPermission] =
    useState<SharePermissionState>("VIEW");

  const [selectedUsers, setSelectedUsers] = useState<ResourcePermissionUser[]>(
    [],
  );

  const [selectedGroups, setSelectedGroups] = useState<
    ResourcePermissionGroup[]
  >([]);

  const workspaceSlug = useWorkspaceSlug();

  // Check for changes in shared users
  useEffect(() => {
    let changed = false;

    // Check if visibility scope has changed
    if (visibilityScope !== scenario?.visibilityScope) {
      changed = true;
    }

    // Check if any users have changed permissions or been marked for removal
    for (const user of sharedUsers) {
      if (
        user.shareStatus === "REMOVE" ||
        user.shareStatus !== user.originalPermissionLevel
      ) {
        changed = true;
        break;
      }
    }

    // Check if any groups have changed permissions or been marked for removal
    if (!changed) {
      for (const group of sharedGroups) {
        if (
          group.shareStatus === "REMOVE" ||
          group.shareStatus !== group.originalPermissionLevel
        ) {
          changed = true;
          break;
        }
      }
    }

    // Use inAddPermissionMode or direct check on selectedUsers/Groups
    setHasChanges(
      changed || selectedUsers.length > 0 || selectedGroups.length > 0,
    );
  }, [
    sharedUsers,
    sharedGroups,
    visibilityScope,
    scenario?.visibilityScope,
    selectedUsers,
    selectedGroups,
  ]);

  const handleClose = () => {
    onClose();
    setSelectedUsers([]);
    setSelectedGroups([]);
    setIsNotified(true);
    setMessage("");
    setSelectedPermission("VIEW");
    setHasChanges(false);
  };

  const handleCopyLink = async () => {
    if (copied) {
      return;
    }
    try {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 5000);
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      setCopied(false);
    }
  };

  const handleShare = () => {
    // Create permissions for newly added users and groups
    if (inAddPermissionMode && scenario?.id) {
      // Prepare users and groups for API
      const emails = selectedUsers.map((user) => user.email).filter(Boolean);
      const groupIds = selectedGroups.map((group) => group.id).filter(Boolean);

      // Format input according to CreateResourcePermissionsInput type
      const input: CreateResourcePermissionsInput = {
        resourceType: ResourceType.Scenario,
        resourceId: scenario.id,
        permissionLevel: selectedPermission as PermissionLevel,
        userEmails: emails,
        groupIds,
        message: isNotified ? message : null,
        isNotified: isNotified,
      };

      createResourcePermissions({ variables: { input } });
    } else {
      // Update existing permissions (removed users/groups, changed access level)
      const userPermissionsToUpdate: ResourcePermissionUser[] = [];
      const groupPermissionsToUpdate: ResourcePermissionGroup[] = [];

      // Process users with "Remove" status or updated permission levels
      sharedUsers.forEach((user) => {
        if (user.id) {
          // Using user entity ID, not permission ID
          if (user.shareStatus === "REMOVE") {
            userPermissionsToUpdate.push({
              ...user,
              remove: true,
            });
          } else if (user.shareStatus !== user.originalPermissionLevel) {
            // Only update if permission level has changed
            userPermissionsToUpdate.push({
              ...user,
            });
          }
        }
      });

      // Process groups with "Remove" status or updated permission levels
      sharedGroups.forEach((group) => {
        if (group.id) {
          // Using group entity ID, not permission ID
          if (group.shareStatus === "REMOVE") {
            groupPermissionsToUpdate.push({
              ...group,
              remove: true,
            });
          } else if (group.shareStatus !== group.originalPermissionLevel) {
            // Only update if permission level has changed
            groupPermissionsToUpdate.push({
              ...group,
            });
          }
        }
      });

      // Format input according to UpdateResourcePermissionsInput type
      const input: UpdateResourcePermissionsInput = {
        resourceType: ResourceType.Scenario,
        resourceId: scenario.id,
        userPermissions:
          userPermissionsToUpdate.length > 0
            ? userPermissionsToUpdate
            : undefined,
        groupPermissions:
          groupPermissionsToUpdate.length > 0
            ? groupPermissionsToUpdate
            : undefined,
        visibilityScope:
          visibilityScope !== scenario?.visibilityScope
            ? visibilityScope
            : undefined,
      };

      updateResourcePermissions({ variables: { input } });
    }
  };

  // Renamed from inNotificationState to inAddPermissionMode
  const inAddPermissionMode =
    selectedUsers.length > 0 || selectedGroups.length > 0;
  const primaryButtonText = inAddPermissionMode
    ? isNotified
      ? "Send"
      : "Share"
    : "Done";

  // Combined loading state
  const loading = externalLoading || createLoading || updateLoading;

  return (
    <>
      <StyledDialog
        isOpen={isOpen}
        usePortal={true}
        lazy={true}
        title={<Box fontSize={18}>Share Scenario</Box>}
        width="450px"
        onClose={handleClose}
      >
        <Box css={{ padding: "16px", gap: 32 }}>
          <Flex>
            <Box flexGrow={1} mr={2}>
              {workspaceSlug && (
                <ScenarioShareMultiselect
                  placeholder="Add people and groups"
                  onChange={(users, groups) => {
                    setSelectedUsers(
                      users as unknown as ResourcePermissionUser[],
                    );
                    setSelectedGroups(
                      groups as unknown as ResourcePermissionGroup[],
                    );
                  }}
                />
              )}
            </Box>

            {inAddPermissionMode && (
              <PermissionSelector
                selectedPermission={selectedPermission}
                onPermissionChange={(permission) => {
                  setSelectedPermission(permission);
                }}
              />
            )}
          </Flex>

          {inAddPermissionMode && (
            <NotificationMessageForm
              isNotified={isNotified}
              message={message}
              onIsNotifiedChange={setIsNotified}
              onMessageChange={setMessage}
            />
          )}

          {!inAddPermissionMode && (
            <Box mt={"24px"}>
              <Box fontSize={16} fontWeight={500}>
                People with access
              </Box>

              {permissionsLoading ? (
                <Box mt={"8px"}>
                  <Spinner intent="primary" />
                </Box>
              ) : (
                <>
                  {sharedUsers?.map((viewer) => (
                    <SharedPill
                      key={viewer?.id || viewer?.email}
                      isMe={viewer?.id === getUser()?.id}
                      isOwner={viewer?.isOwner || false}
                      isGroup={false}
                      item={viewer}
                      shareStatus={viewer?.shareStatus || "VIEW"}
                      onUpdateShareStatus={(shareStatus) => {
                        setSharedUsers((prev) => {
                          return prev.map((u) => {
                            if (u?.email === viewer?.email) {
                              return { ...u, shareStatus };
                            }
                            return u;
                          });
                        });
                      }}
                    />
                  ))}

                  {sharedGroups?.map((group) => (
                    <SharedPill
                      key={group?.id}
                      isMe={false}
                      isOwner={false}
                      isGroup={true}
                      item={group}
                      shareStatus={group?.shareStatus || "VIEW"}
                      onUpdateShareStatus={(shareStatus) => {
                        setSharedGroups((prev) => {
                          return prev.map((g) => {
                            if (g?.id === group?.id) {
                              return { ...g, shareStatus };
                            }
                            return g;
                          });
                        });
                      }}
                    />
                  ))}
                </>
              )}

              {programs?.length > 0 && (
                <Box mt={"16px"}>
                  <Box fontSize={16} fontWeight={500}>
                    Visible to participants in these programs
                  </Box>
                  <Box fontSize={14} color={MIDTEXT_COLOR}>
                    To remove visibility please edit the program
                  </Box>
                  <Box style={{ gap: 16 }} mt={"16px"}>
                    {displayedPrograms.map((program) => (
                      <ScenarioProgram key={program.id} program={program} />
                    ))}

                    {hasMorePrograms && (
                      <Flex
                        justifyContent="center"
                        width="100%"
                        style={{ marginTop: 8 }}
                      >
                        <Button
                          minimal={true}
                          small={true}
                          onClick={() => setShowAllPrograms(!showAllPrograms)}
                        >
                          {showAllPrograms ? "See Less" : `See More`}
                        </Button>
                      </Flex>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {!inAddPermissionMode && (
            <Box mt={"16px"}>
              <Box fontSize={16} fontWeight={500}>
                General Access
              </Box>

              <GeneralAccess
                visibilityScope={visibilityScope}
                onChange={(scope: VisibilityScope) => {
                  setVisibilityScope(scope);
                }}
                canShareToWorkspace={scenario?.canShareToWorkspace}
              />
            </Box>
          )}

          <Flex mt={4} justifyContent="space-between">
            <Button
              icon={
                <Icon
                  icon={copied ? "tick" : "link"}
                  style={{
                    color: copied ? "#0D8050" : "black",
                  }}
                />
              }
              large={true}
              onClick={handleCopyLink}
              style={{
                height: 40,
                background: LIGHT_GRAY,
                boxShadow: "none",
                fontSize: 16,
                color: copied ? "#0D8050" : "black",
              }}
            >
              Copy Link
            </Button>
            <Button
              loading={loading}
              large={true}
              intent="primary"
              disabled={!hasChanges && !inAddPermissionMode}
              onClick={handleShare}
              style={{ height: 40, boxShadow: "none", fontSize: 16 }}
            >
              {primaryButtonText}
            </Button>
          </Flex>
        </Box>
      </StyledDialog>
      <OverlayToaster position={Position.TOP} ref={toasterRef} />
    </>
  );
}
